import * as React from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import styles from '~/components/Popups/purchase_popup.scss';
import Button, { ButtonVariant } from '~/components/Button/Button';
import { t } from '~/utils/localization';
import equal from 'fast-deep-equal/react';
import { Popup, PopupBody, PopupFooter, PopupHeader } from '~/components/Popup/Popup';
import { openUrl, settings } from '~/utils/settings';
import { getPurchaseErrorCodeText } from '@wg/wows-commerce';
import { PURCHASE_ERROR_CODES, PURCHASE_ERRORS_BY_COUNTRY } from '@wg/wows-commerce/constants/purchaseErrorCodes';
import { arrayToObjectByKey } from '~/utils/utils';
import { IPopupSoundset } from '~/types/soundsets';
import { PopupManagerContext } from '~/Contexts/PopupManagerContext';
import { RootState, useAppSelector } from '~/Store';
import { appActions } from '~/Store/appSlice';
import Attention, { AttentionVariant } from '../Attention/Attention';
import { handleClick } from '~/hooks/useDelegationClickForLinkHandler';
import ParentalControlsInformer from '~/components/ParentalControlsInformer/ParentalControlsInformer';

const stateSelector = (state: RootState) => {
    return {
        products: state.wsmart.products,
        billingAddressSettings: state.wsmart.billingAddressSettings,
        bundles: state.app.bundles,
    };
};

const WSMartPurchaseResultPopup = () => {
    const { popup } = React.useContext(PopupManagerContext);
    const dispatch = useDispatch();
    const app = useAppSelector(stateSelector, equal);
    const bundlesMap = arrayToObjectByKey(Object.values(app.bundles), 'productCode');
    const { isSuccess, product, errorCode, bundle } = popup.data;
    let errorMsg: string = null;

    const isErrorByCountry = PURCHASE_ERRORS_BY_COUNTRY.includes(errorCode);
    const isBillingAddressError = errorCode === PURCHASE_ERROR_CODES.ADDRESS_REQUIRED_BUT_NOT_EXIST;
    const isYppError = [PURCHASE_ERROR_CODES.PURCHASE_DENIED, PURCHASE_ERROR_CODES.PAYMENT_LIMIT_EXCEEDED, PURCHASE_ERROR_CODES.LOOTBOXES_DENIED].includes(errorCode);
    const isCustomError = isErrorByCountry || isBillingAddressError;

    if (errorCode) {
        errorMsg = getPurchaseErrorCodeText(errorCode);
        if (isErrorByCountry) {
            errorMsg = errorMsg.replace('{link-open}', '').replace('{link-close}', '');
        }
    }

    const close = () => {
        dispatch(appActions.changeVisibilityPopup({ name: null }));
    };

    const imageUrl = bundlesMap[product.code]?.icons?.small || product.tileImageUrl;
    const backgroundStyle = {
        backgroundImage: `url(${imageUrl})`,
    };

    const classesContentBackground = classNames({
        [styles.purchaseSuccessImageWrapper]: isSuccess,
        [styles.eliteBackground]: isSuccess,
        [styles.purchaseErrorImageWrapper]: !isSuccess,
    });

    return (
        <Popup onClose={close} soundset={isSuccess && IPopupSoundset.EXCHANGE}>
            <PopupHeader title={isSuccess ? t('Поздравляем, покупка прошла успешо!') : isCustomError ? t('Ошибка') : t('Произошла ошибка. Повторите попытку позже')} />
            <PopupBody>
                <div className={styles.purchaseSuccessContent}>
                    {!isCustomError && (
                        <>
                            <div className={classesContentBackground}>
                                <div
                                    className={classNames(styles.popupImage, {
                                        [styles.grayscale]: !isSuccess,
                                    })}
                                    style={backgroundStyle}
                                />
                            </div>
                            <div className={styles.purchaseSuccessContentData}>
                                <div className={styles.purchaseSuccessContentTitle} dangerouslySetInnerHTML={{ __html: bundle?.title || product.title }} />
                            </div>
                        </>
                    )}
                    <div className={styles.errorMessage}>
                        {isYppError ? (
                            <ParentalControlsInformer errorCode={errorCode} />
                        ) : errorMsg ? (
                            <Attention label={errorMsg} variant={AttentionVariant.dark} isHTML={true} onClick={handleClick} />
                        ) : null}
                    </div>
                </div>
            </PopupBody>
            <PopupFooter>
                {isCustomError ? (
                    <div className={styles.buttonsLine}>
                        {isErrorByCountry && <Button className={styles.customErrorButton} linkTarget={true} onClick={() => openUrl(settings.urls.supportUrl)} label={t('Центр поддержки')} />}
                        {isBillingAddressError && (
                            <Button className={styles.customErrorButton} linkTarget={true} onClick={() => openUrl(app.billingAddressSettings.managementUrl)} label={t('Добавить адрес')} />
                        )}
                        <Button onClick={close} label={t('Закрыть')} />
                    </div>
                ) : (
                    <Button variant={ButtonVariant.orange} onClick={close} label={t('Закрыть')} />
                )}
            </PopupFooter>
        </Popup>
    );
};

export default WSMartPurchaseResultPopup;
